import { createContext, ReactNode, useContext } from "react";
import { cn } from "@/libs/utils";
import Image from "next/image";
import Link from "next/link";
import { buttonVariants } from "@/v2-components/ui/button";
import ChevronRightIcon from "@/v2-components/core/icon/chevron-right-icon";
import { useTrans } from "@/utils/useTrans";
import { type VariantProps } from "class-variance-authority";
import { BhapticsContent } from "@/store/reducers/contents";

interface BaseProps {
  children?: React.ReactNode;
}

interface CardContext {
  bhapticsContent: BhapticsContent;
  theme?: "light" | "dark";
}

const BhapticsContentCardContext = createContext<CardContext | null>(null);

const useCardContext = () => {
  const context = useContext(BhapticsContentCardContext);
  if (!context) {
    throw new Error(
      "BhapticsContentCard compound components cannot be rendered outside the BhapticsContentCard component",
    );
  }
  return context;
};

export const useBhapticsContentCardContext = useCardContext;

function Root({
  bhapticsContent,
  theme = "light",
  children,
}: CardContext & BaseProps) {
  return (
    <BhapticsContentCardContext.Provider value={{ bhapticsContent, theme }}>
      {children}
    </BhapticsContentCardContext.Provider>
  );
}

function Thumbnail({
  className,
  src,
  alt = "",
  orientation = "portrait",
  ...props
}: Omit<React.ComponentProps<typeof Image>, "src" | "alt"> & {
  src?: string;
  alt?: string;
  orientation?: "portrait" | "landscape";
}) {
  const { bhapticsContent } = useCardContext();

  return (
    <Image
      className={cn(className)}
      src={
        src ??
        (orientation === "portrait"
          ? bhapticsContent.thumbnailLink
          : bhapticsContent.thumbnailWideLink)
      }
      alt={alt ?? bhapticsContent.name}
      {...props}
    />
  );
}

function Name({ className }: { className?: string }) {
  const { bhapticsContent, theme } = useCardContext();

  return (
    <h2
      className={cn(
        "text-2xl text-coolgray-90",
        theme === "dark" && "text-white",
        className,
      )}
    >
      {bhapticsContent.name}
    </h2>
  );
}

function LearnMore({
  className,
  variant = "blue-text",
  icon = <ChevronRightIcon className="size-4.5" />,
}: {
  icon?: ReactNode;
  className?: string;
  variant?: VariantProps<typeof buttonVariants>["variant"];
}) {
  const { bhapticsContent } = useCardContext();
  const { t } = useTrans(["tactsuit"]);

  return (
    <Link
      href={`/games/experiences/${bhapticsContent.slug}`}
      className={cn(
        buttonVariants({
          variant: variant,
          size: "sm",
        }),
        className,
      )}
    >
      <span>{t("Learn More", { ns: "gnb" })}</span>
      {!!icon && icon}
    </Link>
  );
}

const BhapticsContentCard = Object.assign(Root, {
  Thumbnail,
  LearnMore,
  Name,
});

export default BhapticsContentCard;
